import {
  PaymentLineItemAncillaryCreditEnum,
  PaymentLineItemEnum,
  PaymentLineItemTravelWalletEnum,
  TravelItineraryEnum,
} from "@b2bportal/air-booking-api";
import {
  ExchangeScenarioEnum,
  FtcEnum,
  VoidScenarioEnum,
} from "@b2bportal/air-exchange-api";
import type { FlightState } from "@b2bportal/core-flights";
import {
  CallState,
  type IFlightExchangeLineItem,
  type IFlightExchangeSummaryLineItem,
  SummaryLineType,
  TripCategory,
} from "@b2bportal/core-types";
import { ensureExhaustive } from "@b2bportal/core-utilities";
import { createSelector } from "@reduxjs/toolkit";
import { keyBy, last } from "lodash-es";
import * as FlightPostBookingSelectors from "../../postbooking/store/selectors";

import {
  PREV_FLIGHT_CREDIT_LABEL_KEY,
  FTC_APPLIED_LABEL_KEY,
  SUMMARY_SUBTOTAL_LABEL_KEY,
  SUMMARY_TOTAL_LABEL_KEY,
  TRAVEL_WALLET_CREDIT_LABEL_KEY,
  TRAVEL_WALLET_OFFER_LABEL_KEY,
  PRICE_FREEZE_CREDIT_LABEL_KEY,
  SEATS_SUBTOTAL_LABEL_KEY,
  getAncillariesLabelCopyKey,
} from "../constants";
import { getPaxPricingDetails } from "../utils";
import { getShoppedPaxPricingMap } from "./shopSelectors";

const getPolicyStore = (state: FlightState) =>
  state.flights.flightExchange.policy;

const getPostBookingStore = (state: FlightState) =>
  state.flights.flightPostBooking;

export const getAncillariesPayment = (state: FlightState) =>
  getTotalPricing(state)?.ancillaries;

export const getChangeFee = (state: FlightState) => {
  const policy = getExchangePolicy(state);

  if (policy && "changeFee" in policy) {
    return policy.changeFee;
  }

  return { amount: 0, currency: "" };
};

export const getContextLoading = (state: FlightState) => {
  const { exchangePolicy, itinerary, itineraryFetchState, policyFetchState } =
    getPolicyStore(state);
  const doneLoadingPolicy =
    policyFetchState === CallState.Success && Boolean(exchangePolicy);
  const doneLoadingItinerary =
    itineraryFetchState === CallState.Success && Boolean(itinerary);

  return Boolean(!doneLoadingPolicy || !doneLoadingItinerary);
};

export const getExchangePolicy = (state: FlightState) =>
  getPolicyStore(state).exchangePolicy;

export const getExchangeScenario = (state: FlightState) =>
  getExchangePolicy(state)?.ExchangeScenario;

export const getImportantInfo = createSelector(
  getExchangePolicy,
  (exchangePolicy) => {
    if (exchangePolicy == null) {
      return [];
    } else {
      const policy = exchangePolicy.ExchangeScenario;
      switch (policy) {
        case ExchangeScenarioEnum.Exchangeable:
          return exchangePolicy.exchangeCopy.importantInfo;
        case ExchangeScenarioEnum.Ftc:
          // FtcAvailable is the only option that has `exchangeCopy`, so this will only return if the value is present
          if (exchangePolicy.Ftc === FtcEnum.FtcAvailable) {
            return exchangePolicy.exchangeCopy.importantInfo;
          }
          return [];
        case ExchangeScenarioEnum.AirlineControl:
        case ExchangeScenarioEnum.BookingPending:
        case ExchangeScenarioEnum.Canceled:
        case ExchangeScenarioEnum.CancellationPending:
        case ExchangeScenarioEnum.ContactAirline:
        case ExchangeScenarioEnum.ContactCustomerService:
        case ExchangeScenarioEnum.ContainsRemarks:
        case ExchangeScenarioEnum.Departed:
        case ExchangeScenarioEnum.MultiProvider:
        case ExchangeScenarioEnum.NonExchangeable:
          return [];
        default:
          return ensureExhaustive(policy);
      }
    }
  }
);

// move this to /utils
export const getGatekeeperCopy = (state: FlightState) => {
  const policy = getExchangePolicy(state);

  if (policy == null) return undefined;

  const { ExchangeScenario: scenario } = policy;

  if (scenario === ExchangeScenarioEnum.AirlineControl) {
    return policy.copy;
  } else if (
    scenario === ExchangeScenarioEnum.ContactAirline ||
    scenario === ExchangeScenarioEnum.NonExchangeable
  ) {
    return policy.exchangeCopy;
  } else if (scenario === ExchangeScenarioEnum.ContactCustomerService) {
    return policy.customerServiceCopy;
  }

  return undefined;
};

export const getHasCfar = (state: FlightState) =>
  Boolean(getOriginalItinerary(state)?.ancillaries.cfar);

export const getHasChfar = (state: FlightState) =>
  Boolean(getOriginalItinerary(state)?.ancillaries.chfar);

export const getIsExchangeable = (state: FlightState) => {
  const scenario = getExchangeScenario(state);

  return scenario === ExchangeScenarioEnum.Exchangeable;
};

export const getIsFtc = (state: FlightState) => {
  const scenario = getExchangeScenario(state);

  return scenario === ExchangeScenarioEnum.Ftc;
};

export const getOriginalItinerary = (state: FlightState) => {
  const result = FlightPostBookingSelectors.getSelectedFlightItinerary(state);
  return result.state === CallState.Success ? result.data : undefined;
};

export const getItineraryDepDate = (state: FlightState) => {
  const tItinerary = getTravelItinerary(state);

  if (tItinerary) {
    const { updatedDeparture, zonedUpdatedDeparture } =
      tItinerary.slices[0].segments[0];

    return updatedDeparture ?? zonedUpdatedDeparture;
  }

  return "";
};

export const getItineraryDest = (state: FlightState) => {
  const travelItinerary = getTravelItinerary(state);
  const lastSegment = travelItinerary?.slices[0].segments.at(-1);

  if (lastSegment) return lastSegment.destination;

  return null;
};

export const getItineraryDestName = (state: FlightState) => {
  const airports = getPostBookingStore(state).context.airports;
  const destination = getItineraryDest(state);
  let destinationName = "";

  if (destination) {
    destinationName = airports[destination.locationCode]?.name ?? "";
  }

  return destinationName;
};

export const getItineraryOrigin = (state: FlightState) => {
  const travelItinerary = getTravelItinerary(state);

  return travelItinerary?.slices[0].segments[0].origin;
};

export const getItineraryOriginName = (state: FlightState) => {
  const airports = getPostBookingStore(state).context.airports;
  const origin = getItineraryOrigin(state);
  let originName = "";

  if (origin) originName = airports[origin.locationCode]?.name ?? "";

  return originName;
};

export const getItineraryPNR = (state: FlightState) =>
  getTravelItinerary(state)?.locators?.agent.unscopedValue;

export const getItineraryPaxPricing = (state: FlightState) =>
  getOriginalItinerary(state)?.bookedItinerary.sellingPricing
    .pricingByPassenger;

export const getItineraryPayments = (state: FlightState) =>
  getOriginalItinerary(state)?.bookedItinerary.paymentBreakdown?.payments ?? [];

export const getItineraryRetDate = (state: FlightState) => {
  const tItinerary = getTravelItinerary(state);

  if (tItinerary?.slices && tItinerary?.slices?.length > 1) {
    const returnSlice = last(tItinerary.slices);

    if (returnSlice) {
      const { updatedDeparture, zonedUpdatedDeparture } =
        returnSlice.segments[0];

      return updatedDeparture ?? zonedUpdatedDeparture;
    }
  }

  return "";
};

export const getItinerarySlices = (state: FlightState) =>
  getTravelItinerary(state)?.slices ?? [];

export const getItineraryTripType = (state: FlightState) =>
  getItinerarySlices(state).length > 1
    ? TripCategory.ROUND_TRIP
    : TripCategory.ONE_WAY;

export const getMarketingAirline = (state: FlightState) => {
  const airlines = getPostBookingStore(state).context.airlines;
  const tItinerary = getTravelItinerary(state);

  let airline: string | undefined = undefined;

  if (tItinerary) {
    const { marketingAirline } = tItinerary.slices[0].segments[0];

    airline = airlines[marketingAirline.code]?.displayName;
  }

  return airline;
};

export const getOriginalChangeFee = (state: FlightState) => {
  const policy = getExchangePolicy(state);

  if (policy && "originalChangeFee" in policy) {
    return policy.originalChangeFee;
  }

  return { amount: 0, currency: "" };
};

export const getOriginalPaxPricingMap = createSelector(
  getItineraryPaxPricing,
  (paxPricing) => keyBy(paxPricing, "passengerType")
);

export const getPassengers = (state: FlightState) =>
  getOriginalItinerary(state)?.bookedItinerary.passengers;

export const getPayments = (state: FlightState) =>
  getOriginalItinerary(state)?.bookedItinerary.paymentBreakdown?.payments;
export const getPolicyHasCfar = (state: FlightState) => {
  const policy = getExchangePolicy(state);

  if (policy && "hasCfar" in policy) {
    return policy.hasCfar;
  }

  return false;
};

export const getPrevOutboundSlice = (state: FlightState) =>
  getItinerarySlices(state)[0];

export const getPrevReturnSlice = (state: FlightState) =>
  getItinerarySlices(state)[1];

export const getReservationId = (state: FlightState) =>
  getOriginalItinerary(state)?.bookedItinerary.id;

export const getScenarioCopy = (state: FlightState) => {
  const scenario = getExchangePolicy(state);
  const voidPolicy = getVoidPolicy(state);
  let exchangeScenario;
  let voidScenario;

  if (scenario) {
    ({ ExchangeScenario: exchangeScenario } = scenario);
  }

  if (voidPolicy) {
    ({ VoidScenario: voidScenario } = voidPolicy);
  }

  if (
    exchangeScenario === ExchangeScenarioEnum.Exchangeable ||
    exchangeScenario === ExchangeScenarioEnum.Ftc
  ) {
    return exchangeScenario;
  }

  if (
    (voidScenario === VoidScenarioEnum.PreTicketVoidable ||
      voidScenario === VoidScenarioEnum.TicketedVoid) &&
    "exchangeableScenario" in exchangeScenario
  ) {
    return exchangeScenario.exchangeableScenario;
  }

  return null;
};

export const getSeatPayments = (state: FlightState) =>
  getOriginalItinerary(state)?.bookedItinerary.seats?.seats;

export const getSubTotalPayment = (state: FlightState) =>
  getTotalPricing(state)?.subtotal;

export const getTotalPricing = (state: FlightState) =>
  getOriginalItinerary(state)?.bookedItinerary.sellingPricing.totalPricing;

export const getTravelCredit = (state: FlightState) =>
  getOriginalItinerary(state)?.travelCredit;

export const getTravelItinerary = (state: FlightState) => {
  const itinerary = getOriginalItinerary(state);

  if (itinerary) {
    const { travelItinerary } = itinerary.bookedItinerary;

    if (
      travelItinerary.TravelItinerary ===
      TravelItineraryEnum.SingleTravelItinerary
    ) {
      return travelItinerary;
    }

    return travelItinerary.travelItineraries[0];
  }

  return null;
};

export const getVoidPolicy = (state: FlightState) =>
  getPolicyStore(state).voidPolicy;

export const getWaiveRebookingFee = (state: FlightState) => {
  const isFTC = getIsFtc(state);
  const hasCfar = getHasCfar(state);
  const hasChfar = getHasChfar(state);

  return Boolean((isFTC && hasCfar) || (!isFTC && hasChfar));
};

export const getOriginalBookingPaymentInfo = createSelector(
  getAncillariesPayment,
  getPayments,
  getSeatPayments,
  getSubTotalPayment,
  (ancillaryPayments, payments, seatPayments, subtotalPayment) => {
    const paymentInfo = {
      cardInfo: "",
      pfCredit: 0,
      payments: [] as IFlightExchangeLineItem[],
    };
    let prevAirfareAmt = 0;
    let prevAirfareCurrency = "";

    if (subtotalPayment) {
      const { currencyCode, value } = subtotalPayment.fiat;

      prevAirfareAmt = value;
      prevAirfareCurrency = currencyCode;
    }

    if (seatPayments?.length != null) {
      const seatCurrency = seatPayments[0].currency;
      const seatsTotal = seatPayments.reduce(
        (acc, seat) => acc + seat.price,
        0
      );

      if (seatsTotal > 0 && seatCurrency) {
        paymentInfo.payments.push({
          amount: seatsTotal,
          currency: seatCurrency,
          label: SEATS_SUBTOTAL_LABEL_KEY,
        });
      }
    }

    if (ancillaryPayments != null) {
      for (const payment of ancillaryPayments) {
        const {
          kind,
          premium: {
            fiat: { currencyCode, value },
          },
        } = payment;

        if (value > 0 && currencyCode) {
          paymentInfo.payments.push({
            amount: value,
            currency: currencyCode,
            label: getAncillariesLabelCopyKey(kind),
          });
        }
      }
    }

    if (payments?.length != null) {
      for (const payment of payments) {
        const { PaymentLineItem } = payment;
        let amount = 0;
        let currency = "";
        let label = "";

        if (PaymentLineItem === PaymentLineItemEnum.AncillaryCredit) {
          ({
            AncillaryCredit: label,
            amount: { amount, currency },
          } = payment);

          if (label === PaymentLineItemAncillaryCreditEnum.PriceFreezeCredit) {
            paymentInfo.pfCredit = amount;
            /*
             * subtotal includes price freeze credit already so it needs to be
             * added back in to get the total airfare value
             */
            prevAirfareAmt += amount;
            amount *= -1; // price freeze credit is a deduction in prev payment
            label = PRICE_FREEZE_CREDIT_LABEL_KEY;
          }
        } else if (PaymentLineItem === PaymentLineItemEnum.TravelWallet) {
          ({
            TravelWallet: label,
            amount: { amount, currency },
          } = payment);

          if (label === PaymentLineItemTravelWalletEnum.TravelWalletCredit) {
            label = TRAVEL_WALLET_CREDIT_LABEL_KEY;
          } else if (
            label === PaymentLineItemTravelWalletEnum.TravelWalletOffer
          ) {
            label = TRAVEL_WALLET_OFFER_LABEL_KEY;
          }

          amount *= -1; // wallet credits/offers are deductions
        }

        if (amount !== 0 && currency && label) {
          paymentInfo.payments.push({ amount, currency, label });
        }
      }
    }

    if (prevAirfareAmt > 0 && prevAirfareCurrency) {
      paymentInfo.payments.unshift({
        amount: prevAirfareAmt,
        currency: prevAirfareCurrency,
        label: PREV_FLIGHT_CREDIT_LABEL_KEY,
      });
    }

    return paymentInfo;
  }
);

export const getNewBookingLineItems = createSelector(
  getChangeFee,
  getOriginalBookingPaymentInfo,
  getOriginalPaxPricingMap,
  getPassengers,
  getShoppedPaxPricingMap,
  getWaiveRebookingFee,
  (
    changeFee,
    originalPaymentInfo,
    originalPaxPricingMap,
    passengers,
    shoppedPaxPricingMap,
    waiveRebookingFee
  ) => {
    const lineItems: IFlightExchangeLineItem[] = [];
    let changeFeeAmt = 0;

    if (waiveRebookingFee && changeFee?.amount != null) {
      changeFeeAmt = changeFee.amount;
    }

    if (passengers) {
      const { alone, withLapInfants } = passengers;
      let pfCreditAmt = 0;

      if (originalPaymentInfo.pfCredit > 0) {
        const numTravelers = alone.length + withLapInfants.length;

        pfCreditAmt = originalPaymentInfo.pfCredit / numTravelers;
      }

      for (const pax of alone) {
        const {
          person: { givenName, surname },
          type,
        } = pax;
        const { baseAmount, currency, taxes } = getPaxPricingDetails(
          originalPaxPricingMap,
          shoppedPaxPricingMap,
          type
        );

        lineItems.push({
          currency,
          amount: baseAmount + taxes + pfCreditAmt + changeFeeAmt,
          label: `${givenName} ${surname}`,
        });
      }

      for (const pax of withLapInfants) {
        const {
          adult: {
            person: { givenName: adtFirstName, surname: adtSurname },
            type,
          },
          infant: {
            person: { givenName: infFirstName, surname: infSurname },
          },
        } = pax;
        const { baseAmount, currency, taxes } = getPaxPricingDetails(
          originalPaxPricingMap,
          shoppedPaxPricingMap,
          type
        );

        lineItems.push(
          {
            currency,
            amount: baseAmount + taxes + changeFeeAmt,
            label: `${adtFirstName} ${adtSurname}`,
          },
          {
            currency,
            amount: changeFeeAmt,
            label: `${infFirstName} ${infSurname}`,
          }
        );
      }
    }

    return lineItems;
  }
);

export const getSummaryLineItems = createSelector(
  getIsFtc,
  getNewBookingLineItems,
  getOriginalBookingPaymentInfo,
  getTravelCredit,
  (isFtc, newPayments, originalPaymentInfo, travelCredit) => {
    const { payments: originalPayments } = originalPaymentInfo;
    const shoppedSubtotal = newPayments.reduce(
      (sum, payment) => sum + payment.amount,
      0
    );
    const summaryLineItems: IFlightExchangeSummaryLineItem[] = [
      {
        amount: shoppedSubtotal,
        currency: newPayments[0]?.currency || originalPayments[0]?.currency,
        label: isFtc ? SUMMARY_TOTAL_LABEL_KEY : SUMMARY_SUBTOTAL_LABEL_KEY,
        type: isFtc ? SummaryLineType.TOTAL : SummaryLineType.SUB_TOTAL,
      },
    ];

    if (isFtc && travelCredit) {
      const { amount, currency } = travelCredit.credit;

      summaryLineItems.push({
        currency,
        amount: -1 * amount,
        label: FTC_APPLIED_LABEL_KEY,
        type: SummaryLineType.CARD_PAYMENT,
      });
    } else if (
      !isFtc &&
      // this payment is added to the front of the list so only need to check this one element
      originalPayments[0]?.label === PREV_FLIGHT_CREDIT_LABEL_KEY
    ) {
      summaryLineItems.push({
        // display deduction of original flight booking cost
        amount: -1 * originalPayments[0].amount,
        currency: originalPayments[0].currency,
        label: PREV_FLIGHT_CREDIT_LABEL_KEY,
        type: SummaryLineType.CARD_PAYMENT,
      });
    }

    return summaryLineItems;
  }
);
